/**
 * ResourceEditorPage
 * Full-page CMS-style layout for resource create/edit with two-column grid.
 * Gaps addressed: G2-1 (toolbar fix), G8-1 (tags), G10-1/G10-2 (authors),
 * G12-1 (cover image in payload), G12-2 (publish), G12-3 (delete), C9-1-C9-5 (cover image)
 */

import { useState, useCallback } from 'react';
import type { Editor } from '@tiptap/react';
import { ArrowLeft, Save, AlertCircle, X } from 'lucide-react';
import { TipTapEditor } from './TipTapEditor';
import { TipTapToolbar } from './TipTapToolbar';
import { ResourceEditorForm } from './ResourceEditorForm';
import { ResourceEditorSidebar } from './ResourceEditorSidebar';
import { CoverImageUpload } from './CoverImageUpload';
import { ResourceVideoUpload } from './ResourceVideoUpload';
import { useResourceEditorState } from '../hooks/useResourceEditorState';
import { Skeleton } from '@/shared/components/ui/skeleton';

export function ResourceEditorPage() {
  const [editorInstance, setEditorInstance] = useState<Editor | null>(null);

  const handleEditorReady = useCallback((editor: Editor | null) => {
    if (editor) setEditorInstance(editor);
  }, []);

  const {
    isEditMode,
    isLoadingResource,
    isSaving,
    isPublished,
    resource,
    formValues,
    content,
    coverImage,
    videoFile,
    selectedTypeName,
    errors,
    saveError,
    typeOptions,
    categoryOptions,
    tagOptions,
    authorOptions,
    initialAuthorProNames,
    handleFieldChange,
    handleContentChange,
    handleCoverImageChange,
    handleVideoFileChange,
    handleSave,
    handleBack,
    handleTogglePublish,
    handleDelete,
    handleCreateTag,
    dismissSaveError,
  } = useResourceEditorState();

  if (isEditMode && isLoadingResource) {
    return (
      <div data-testid="editor-page-skeleton" className="space-y-4 p-6">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header bar */}
      <div className="sticky top-0 z-20 bg-white border-b border-gray-200 px-6 py-3 flex items-center justify-between">
        <button
          onClick={handleBack}
          className="flex items-center gap-1.5 text-sm font-medium text-[#026857] hover:text-[#024d43] transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
          Back
        </button>
        <div className="flex items-center gap-3">
          <button
            onClick={handleBack}
            className="px-4 py-2 text-sm border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
            aria-label="Cancel"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={isSaving}
            className="flex items-center gap-1.5 px-4 py-2 text-sm bg-[#026857] text-white rounded-md hover:bg-[#024d43] transition-colors disabled:opacity-50"
            aria-label="Save"
          >
            <Save className="h-4 w-4" />
            {isSaving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>

      {/* Page title + content */}
      <div className="container mx-auto max-w-6xl px-6 py-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-6">
          {isEditMode ? formValues.title || 'Edit Resource' : 'Create Resource'}
        </h1>

        {saveError && (
          <div className="mb-6 rounded-md bg-red-50 border border-red-200 p-4" role="alert">
            <div className="flex">
              <AlertCircle className="h-5 w-5 text-red-400 flex-shrink-0 mt-0.5" />
              <div className="ml-3 flex-1">
                <h3 className="text-sm font-medium text-red-800">
                  {saveError.message}
                </h3>
                {saveError.fieldErrors.length > 0 && (
                  <ul className="mt-2 text-sm text-red-700 list-disc list-inside space-y-1">
                    {saveError.fieldErrors.map((err, i) => (
                      <li key={i}>{err}</li>
                    ))}
                  </ul>
                )}
              </div>
              <button
                type="button"
                onClick={dismissSaveError}
                className="ml-3 flex-shrink-0 text-red-400 hover:text-red-600"
                aria-label="Dismiss error"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>
        )}

        {/* Two-column grid */}
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Main column */}
          <div className="lg:col-span-3">
            <ResourceEditorForm
              values={formValues}
              onChange={handleFieldChange}
              typeOptions={typeOptions}
              categoryOptions={categoryOptions}
              tagOptions={tagOptions}
              authorOptions={authorOptions}
              initialAuthorProNames={initialAuthorProNames}
              onCreateTag={handleCreateTag}
              errors={errors}
              contentSlot={
                <div className="space-y-1">
                  <span className="block text-sm font-medium text-gray-700">Content *</span>
                  {errors.content && <p className="text-sm text-red-600">{errors.content}</p>}
                  <div className="rounded-md border border-gray-300 overflow-hidden">
                    <TipTapToolbar editor={editorInstance} />
                    <TipTapEditor
                      content={content}
                      onChange={handleContentChange}
                      onEditorReady={handleEditorReady}
                    />
                  </div>
                </div>
              }
              mediaSlot={
                <div className="space-y-5">
                  <CoverImageUpload
                    coverImage={coverImage}
                    onCoverImageChange={handleCoverImageChange}
                  />
                  {selectedTypeName === 'Video' && (
                    <ResourceVideoUpload
                      videoFile={videoFile}
                      onVideoFileChange={handleVideoFileChange}
                    />
                  )}
                </div>
              }
            />
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1">
            <div className="sticky top-20">
              <ResourceEditorSidebar
                isPublished={isPublished}
                isEditMode={isEditMode}
                createdAt={resource?.createdAt}
                updatedAt={resource?.updatedAt}
                onTogglePublish={handleTogglePublish}
                onDelete={handleDelete}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
